import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import api from '../../services/api';
import { Client, Question, QuestionAnswer } from '../../types';
import RatingCheckbox from './RatingCheckbox';
import { formatPhoneInput } from '../../utils/phoneFormatter';
import { formatRating, roundRating } from '../../utils/rating';
import './PublicReviewForm.css';

const getStorageUrl = (path: string) => {
  if (!path) return '';
  if (path.startsWith('data:') || path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  const base = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '') || window.location.origin;
  return `${base}/storage/${path}`;
};

const PublicReviewForm: React.FC = () => {
  const navigate = useNavigate();
  const { uniqId } = useParams<{ uniqId: string }>();
  const [searchParams] = useSearchParams();
  const [client, setClient] = useState<Client | null>(null);
  const [clientLogo, setClientLogo] = useState<string | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionAnswers, setQuestionAnswers] = useState<{ [key: number]: number }>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showLowRatingForm, setShowLowRatingForm] = useState(false);
  const [average, setAverage] = useState<number | null>(null);
  const [extraComment, setExtraComment] = useState('');
  const [contactBack, setContactBack] = useState(false);
  const [promoEmails, setPromoEmails] = useState(false);
  const [promoTexts, setPromoTexts] = useState(false);
  const [googleReviewContent, setGoogleReviewContent] = useState<string | null>(null);
  const [googleReviewLink, setGoogleReviewLink] = useState<string | null>(null);
  const [facebookReviewLink, setFacebookReviewLink] = useState<string | null>(null);
  const [customReviewLink, setCustomReviewLink] = useState<string | null>(null);
  const [customReviewTitle, setCustomReviewTitle] = useState<string>('Leave a Review');
  const [backgroundColor, setBackgroundColor] = useState<string>('#667eea');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState<string>('6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI');
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  
  // Question labels from settings (fallback for old reviews)
  const [questionLabels, setQuestionLabels] = useState({
    last_service: 'Please Rate the Last Service',
    quality_service: 'Please Rate the Quality Service',
    refer_business: 'Would you refer this business to a friend or family?',
    work_again: 'Would you work with this business again?',
  });

  // Review threshold (0-10 scale, default 7.0 which is 70/10)
  const [reviewThreshold, setReviewThreshold] = useState<number>(7.0);

  // Comment length limit (in characters), per-client setting `comment_count`
  const [commentCountLimit, setCommentCountLimit] = useState<number>(150);

  const [formData, setFormData] = useState({
    uniq_id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    rating: 0,
    comment: '',
    last_service: 0,
    quality_service: 0,
    refer_business: 0,
    work_again: 0,
  });

  // Fetch reCAPTCHA site key (same pattern as Login page)
  useEffect(() => {
    api.get<{ site_key: string }>('/public/recaptcha-site-key')
      .then(response => {
        if (response.data?.site_key) {
          setRecaptchaSiteKey(response.data.site_key);
        }
      })
      .catch(err => {
        console.error('Failed to fetch reCAPTCHA site key:', err);
      });
  }, []);

  useEffect(() => {
    if (!uniqId) {
      setError('Client ID is required. Please use a valid review link.');
      setLoading(false);
      return;
    }

    // Get query parameters for name and email
    const firstNameParam = searchParams.get('first_name') || searchParams.get('name');
    const lastNameParam = searchParams.get('last_name') || '';
    const emailParam = searchParams.get('email');

    // Auto-populate form fields from query parameters
    // Legacy ?name=Full+Name: put entire value in first_name when last_name is absent
    const legacyFullName = searchParams.get('name') && !searchParams.get('first_name');
    let firstName = firstNameParam || '';
    let lastName = lastNameParam;
    if (legacyFullName && firstNameParam) {
      const parts = firstNameParam.trim().split(/\s+/);
      firstName = parts[0] || '';
      lastName = parts.slice(1).join(' ');
    }

    setFormData(prev => ({
      ...prev,
      uniq_id: uniqId,
      first_name: firstName || prev.first_name,
      last_name: lastName || prev.last_name,
      email: emailParam || prev.email,
    }));

    fetchClient(uniqId);
  }, [uniqId, searchParams]);

  const fetchClient = async (uniqId: string) => {
    try {
      setLoading(true);
      const response = await api.get<Client>(`/public/clients/uniq/${uniqId}`);
      const clientData = response.data;
      setClient(clientData);
      
      // Fetch questions for this client
      if (clientData.id) {
        await fetchQuestions(clientData.id);
        await fetchQuestionLabels(clientData.id);
      }
    } catch (error: any) {
      console.error('Failed to fetch client:', error);
      if (error.response?.status === 404) {
        setError('Client not found. Please use a valid review link.');
      } else {
        setError('Failed to load client information. Please refresh the page.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async (clientId: number) => {
    try {
      const response = await api.get<Question[]>('/public/questions', {
        params: { client_id: clientId },
      });
      setQuestions(response.data);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
    }
  };

  const fetchQuestionLabels = async (clientId: number) => {
    try {
      const response = await api.get(`/public/settings?client_id=${clientId}`);
      const settings = response.data;
      
      // Default labels (fallback if settings are not found)
      const defaultLabels = {
        last_service: 'Please Rate the Last Service',
        quality_service: 'Please Rate the Quality Service',
        refer_business: 'Would you refer this business to a friend or family?',
        work_again: 'Would you work with this business again?',
      };
      
      // Build labels object from settings, using defaults if settings don't exist
      const newLabels = {
        last_service: settings.find((s: any) => s.key === 'last_service')?.value || defaultLabels.last_service,
        quality_service: settings.find((s: any) => s.key === 'quality_service')?.value || defaultLabels.quality_service,
        refer_business: settings.find((s: any) => s.key === 'refer_business')?.value || defaultLabels.refer_business,
        work_again: settings.find((s: any) => s.key === 'work_again')?.value || defaultLabels.work_again,
      };
      
      setQuestionLabels(newLabels);

      // Fetch review_threshold setting and convert from 0-100 scale to 0-10 scale
      const reviewThresholdSetting = settings.find((s: any) => s.key === 'review_threshold');
      if (reviewThresholdSetting && reviewThresholdSetting.value) {
        const thresholdValue = parseFloat(reviewThresholdSetting.value);
        // Convert from 0-100 scale to 0-10 scale (e.g., 70 becomes 7.0, 65 becomes 6.5)
        const thresholdOn10Scale = thresholdValue / 10;
        setReviewThreshold(thresholdOn10Scale);
      }

      const logoSetting = settings.find((s: any) => s.key === 'client_logo');
      const logoValue = typeof logoSetting?.value === 'string' ? logoSetting.value.trim() : '';
      setClientLogo(logoValue ? getStorageUrl(logoValue) : null);

      // Fetch comment_count setting (max comment chars)
      const commentCountSetting = settings.find((s: any) => s.key === 'comment_count');
      if (commentCountSetting && commentCountSetting.value !== undefined && commentCountSetting.value !== null) {
        const parsed = parseInt(String(commentCountSetting.value), 10);
        if (!Number.isNaN(parsed) && parsed >= 0) {
          setCommentCountLimit(parsed);
        }
      }

      const bgColorSetting = settings.find((s: any) => s.key === 'public_review_background_color');
      const bgColor = typeof bgColorSetting?.value === 'string' ? bgColorSetting.value.trim() : '';
      if (bgColor && /^#[0-9A-Fa-f]{6}$/.test(bgColor)) {
        setBackgroundColor(bgColor);
      }
    } catch (error) {
      console.error('Failed to fetch question labels:', error);
      // If it fails, keep default labels (already set in initial state)
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    // Format phone number as user types
    if (name === 'phone') {
      const formatted = formatPhoneInput(value);
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= commentCountLimit) {
      setFormData(prev => ({ ...prev, comment: value }));
    }
  };

  const calculateAverage = (): number => {
    // Use question answers if available, otherwise fall back to old fields
    const answers = Object.values(questionAnswers).filter(v => v > 0);
    if (answers.length > 0) {
      const sum = answers.reduce((acc, val) => acc + val, 0);
      return sum / answers.length;
    }
    
    // Fallback to old fields for backward compatibility
    const sum = 
      Number(formData.last_service) + 
      Number(formData.quality_service) + 
      Number(formData.refer_business) + 
      Number(formData.work_again);
    const count = [formData.last_service, formData.quality_service, formData.refer_business, formData.work_again]
      .filter(v => v > 0).length;
    return count > 0 ? sum / count : 0;
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate reCAPTCHA
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA verification.');
      return;
    }

    // Calculate average
    const calculatedAverage = calculateAverage();
    setAverage(calculatedAverage);

    // If average is less than review threshold, show additional form
    if (calculatedAverage < reviewThreshold) {
      // Reset reCAPTCHA for the low rating form so user completes it again
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
      setShowLowRatingForm(true);
      return;
    }

    // If average is >= review threshold, submit directly
    await submitReview(calculatedAverage);
  };

  const fetchGoogleReviewContent = async (clientId: number) => {
    try {
      const response = await api.get(`/public/settings?client_id=${clientId}`);
      const settings = response.data;
      
      const googleReviewSetting = settings.find((setting: any) => setting.key === 'google_review_content');
      if (googleReviewSetting && googleReviewSetting.value) {
        setGoogleReviewContent(googleReviewSetting.value);
      }
      
      const googleReviewLinkSetting = settings.find((setting: any) => setting.key === 'google_review_link');
      if (googleReviewLinkSetting && googleReviewLinkSetting.value) {
        setGoogleReviewLink(googleReviewLinkSetting.value);
      }
      
      const facebookReviewLinkSetting = settings.find((setting: any) => setting.key === 'facebook_review_link');
      if (facebookReviewLinkSetting && facebookReviewLinkSetting.value) {
        setFacebookReviewLink(facebookReviewLinkSetting.value);
      }

      const customReviewLinkSetting = settings.find((setting: any) => setting.key === 'custom_review_link');
      if (customReviewLinkSetting && customReviewLinkSetting.value) {
        setCustomReviewLink(customReviewLinkSetting.value);
      }

      const customReviewTitleSetting = settings.find((setting: any) => setting.key === 'custom_review_title');
      if (customReviewTitleSetting && customReviewTitleSetting.value) {
        setCustomReviewTitle(customReviewTitleSetting.value);
      }
    } catch (error) {
      console.error('Failed to fetch review settings:', error);
      // If it fails, we'll just show the default message
    }
  };

  const submitReview = async (calculatedAverage: number) => {
    setSubmitting(true);
    setError('');

    // Validate reCAPTCHA (same as Login page)
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA verification.');
      setSubmitting(false);
      return;
    }

    try {
      // Use average directly as rating
      const rating = roundRating(calculatedAverage);

      // Combine comment with extra comment if provided
      const finalComment = showLowRatingForm && extraComment 
        ? `${formData.comment}\n\nAdditional Feedback: ${extraComment}` 
        : formData.comment;

      // Build question answers array
      const questionAnswersArray: QuestionAnswer[] = Object.entries(questionAnswers)
        .filter(([_, rating]) => rating > 0)
        .map(([questionId, rating]) => ({
          question_id: parseInt(questionId, 10),
          rating: rating,
        }));

      // Build payload - exclude old rating fields when using dynamic questions
      const payload: any = {
        uniq_id: formData.uniq_id,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        rating: rating,
        comment: finalComment,
        recaptcha_token: recaptchaToken,
        promo_emails: promoEmails,
        promo_texts: promoTexts,
        question_answers: questionAnswersArray,
      };

      // Only include old rating fields if we're using fallback (no dynamic questions)
      // and the fields have values > 0
      if (questions.length === 0) {
        // Using fallback - include old fields only if they have values > 0
        if (formData.last_service > 0) payload.last_service = Number(formData.last_service);
        if (formData.quality_service > 0) payload.quality_service = Number(formData.quality_service);
        if (formData.refer_business > 0) payload.refer_business = Number(formData.refer_business);
        if (formData.work_again > 0) payload.work_again = Number(formData.work_again);
      }
      // If using dynamic questions, don't include old fields at all (they'll be null in DB)

      await api.post('/public/reviews', payload);

      setSuccess(true);
      // Reset reCAPTCHA after successful submission (same as Login on error)
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
      
      if (calculatedAverage >= reviewThreshold && client) {
        // Fetch google review content for rating >= threshold
        await fetchGoogleReviewContent(client.id);
      } else {
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    } catch (err: any) {
      console.error('Review submission error:', err);
      const data = err.response?.data;
      let errorMessage = data?.message || err.message || 'Failed to submit review. Please try again.';
      if (data?.errors && typeof data.errors === 'object') {
        const firstError = Object.values(data.errors).flat();
        if (firstError?.length && firstError[0]) {
          errorMessage = String(firstError[0]);
        }
      }
      setError(errorMessage);
      
      // Reset reCAPTCHA on error (same as Login page)
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLowRatingSubmit = async () => {
    // Validate reCAPTCHA
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA verification.');
      return;
    }
    await submitReview(average || 0);
  };

  // reCAPTCHA handlers (same as Login page)
  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleRecaptchaError = () => {
    setRecaptchaToken(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const clientLogoMarkup = clientLogo ? (
    <div className="public-review-logo">
      <img
        src={clientLogo}
        alt={client?.client_name ? `${client.client_name} logo` : 'Company logo'}
      />
    </div>
  ) : null;

  const containerStyle: React.CSSProperties = {
    background: backgroundColor,
  };

  if (success) {
    const showReviewButtons = average !== null && average >= reviewThreshold;
    const customReviewButton = customReviewLink ? (
      <a
        href={customReviewLink}
        target="_blank"
        rel="noopener noreferrer"
        className="review-button custom-review"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h2v-6h-2v6zm0-8h2V7h-2v2z" fill="white"/>
        </svg>
        {customReviewTitle || 'Leave a Review'}
      </a>
    ) : null;
    
    return (
      <div className="public-review-container" style={containerStyle}>
        <div className="success-message">
          {clientLogoMarkup}
          {showReviewButtons && googleReviewContent ? (
            <>
              <div 
                className="google-review-content"
                dangerouslySetInnerHTML={{ __html: googleReviewContent }}
              />
              {(googleReviewLink || facebookReviewLink || customReviewLink) && (
                <div className="review-buttons-container">
                  <div className="review-buttons">
                    {googleReviewLink && (
                      <a
                        href={googleReviewLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="review-button google-review"
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="white"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="white"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="white"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="white"/>
                        </svg>
                        Leave a Google Review
                      </a>
                    )}
                    {facebookReviewLink && (
                      <a
                        href={facebookReviewLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="review-button facebook-review"
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="white"/>
                        </svg>
                        Leave a Facebook Review
                      </a>
                    )}
                    {customReviewButton}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <h2>Thank You!</h2>
              <p>Your review has been submitted successfully.</p>
              {showReviewButtons ? (
                <div className="review-buttons-container">
                  <p>We'd love to hear more from you! Please leave a review on:</p>
                  <div className="review-buttons">
                    {googleReviewLink ? (
                      <a
                        href={googleReviewLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="review-button google-review"
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="white"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="white"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="white"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="white"/>
                        </svg>
                        Leave a Google Review
                      </a>
                    ) : (
                      <a
                        href={`https://search.google.com/local/writereview?placeid=${client?.id || ''}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="review-button google-review"
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="white"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="white"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="white"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="white"/>
                        </svg>
                        Leave a Google Review
                      </a>
                    )}
                    {facebookReviewLink ? (
                      <a
                        href={facebookReviewLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="review-button facebook-review"
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="white"/>
                        </svg>
                        Leave a Facebook Review
                      </a>
                    ) : (
                      <a
                        href={`https://www.facebook.com/${client?.id || ''}/reviews`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="review-button facebook-review"
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="white"/>
                        </svg>
                        Leave a Facebook Review
                      </a>
                    )}
                    {customReviewButton}
                  </div>
                </div>
              ) : (
                <p>Redirecting to home page...</p>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="public-review-container" style={containerStyle}>
        <div className="public-review-card">
          <div className="loading-message">Loading review form...</div>
        </div>
      </div>
    );
  }

  if (error && !client) {
    return (
      <div className="public-review-container" style={containerStyle}>
        <div className="public-review-card">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-review-container" style={containerStyle}>
      <div className="public-review-card">
        {clientLogoMarkup}
        {client && (
          <div className="client-heading">
            <h1>Review for {client.client_name}</h1>
          </div>
        )}
        <p className="subtitle">We value your feedback. Please share your experience with us.</p>

        {error && <div className="alert alert-error">{error}</div>}

        {!showLowRatingForm && (
          <form onSubmit={handleFormSubmit} className="review-form">

            <div className="form-group">
              <label htmlFor="first_name">First Name *</label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                placeholder="Enter first name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="last_name">Last Name *</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                placeholder="Enter last name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="your.email@example.com"
              />
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={promoEmails}
                    onChange={(e) => setPromoEmails(e.target.checked)}
                  />
                  <span>Would you like to get promo emails?</span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="999-999-9999"
                maxLength={12}
              />
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={promoTexts}
                    onChange={(e) => setPromoTexts(e.target.checked)}
                  />
                  <span>Would you like to get promo texts?</span>
                </label>
              </div>
            </div>

              {/* Render dynamic questions */}
              {questions.length > 0 ? (
                questions.map((question) => (
                  <RatingCheckbox
                    key={question.id}
                    label={question.question_title}
                    value={questionAnswers[question.id] || 0}
                    onChange={(value) => {
                      setQuestionAnswers(prev => ({
                        ...prev,
                        [question.id]: value,
                      }));
                    }}
                  />
                ))
              ) : (
                /* Fallback to old hardcoded questions if no questions found */
                <>
                  <RatingCheckbox
                    label={questionLabels.last_service}
                    value={formData.last_service}
                    onChange={(value) => setFormData(prev => ({ ...prev, last_service: value }))}
                  />

                  <RatingCheckbox
                    label={questionLabels.quality_service}
                    value={formData.quality_service}
                    onChange={(value) => setFormData(prev => ({ ...prev, quality_service: value }))}
                  />

                  <RatingCheckbox
                    label={questionLabels.refer_business}
                    value={formData.refer_business}
                    onChange={(value) => setFormData(prev => ({ ...prev, refer_business: value }))}
                  />

                  <RatingCheckbox
                    label={questionLabels.work_again}
                    value={formData.work_again}
                    onChange={(value) => setFormData(prev => ({ ...prev, work_again: value }))}
                  />
                </>
              )}

            <div className="form-group">
              <label htmlFor="comment">Comment *</label>
              <textarea
                id="comment"
                name="comment"
                value={formData.comment}
                onChange={handleCommentChange}
                required
                rows={4}
                placeholder={`Share your experience (max ${commentCountLimit} characters)`}
                  maxLength={commentCountLimit}
              />
              <div className="char-counter">
                {formData.comment.length} / {commentCountLimit}
              </div>
            </div>

            <div className="form-group">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={recaptchaSiteKey}
                onChange={handleRecaptchaChange}
                onExpired={handleRecaptchaExpired}
                onErrored={handleRecaptchaError}
              />
            </div>

            <button type="submit" disabled={submitting || loading || !recaptchaToken} className="submit-button">
              {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
          </form>
        )}

        {showLowRatingForm && (
          <div className="low-rating-form">
            <div className="low-rating-message">
              <h3>We'd like to hear more from you</h3>
              <p>Your average rating is {formatRating(average)}/10. Please share additional feedback to help us improve.</p>
            </div>
            
            <div className="form-group">
              <label htmlFor="extraComment">Additional Comments</label>
              <textarea
                id="extraComment"
                name="extraComment"
                value={extraComment}
                onChange={(e) => setExtraComment(e.target.value)}
                rows={4}
                placeholder="Please share any additional feedback or concerns..."
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={contactBack}
                  onChange={(e) => setContactBack(e.target.checked)}
                />
                <span>I would like to be contacted to discuss my experience</span>
              </label>
            </div>

            <div className="form-group">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={recaptchaSiteKey}
                onChange={handleRecaptchaChange}
                onExpired={handleRecaptchaExpired}
                onErrored={handleRecaptchaError}
              />
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={handleLowRatingSubmit}
                disabled={submitting || !recaptchaToken}
                className="submit-button"
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicReviewForm;

